import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

export interface FlagKeyword {
    id?: number;
    keyword?: string;
}

interface FlagKeywordState {
    flagkeyworddata: FlagKeyword[];
    info: {
        count: number;
        current_page: number;
        limit: number;
        total_page: number;
    } | null;
    error: string | null;
    loading: boolean;
}

export const GetallFlagKeyword = createAsyncThunk<
    {
        data: FlagKeyword[];
        info: {
            count: number;
            current_page: number;
            limit: number;
            total_page: number;
        };
    },
    { search: string; page: number; limit: number },
    { rejectValue: string }
>(
    "GetallFlagKeyword",
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            await delay(1000)
            const response = await axiosInstance.get(`/api/admin/flag-keyword`, {
                params: { search, page, limit },
            });
            return { data: response.data.data, info: response.data.info };
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);


// ==================AddFlagKeyword-action-start===========
export const AddFlagKeyword = createAsyncThunk<
    FlagKeyword,
    FlagKeyword,
    { rejectValue: string }
>("flagkeyworddata/AddFlagKeyword", async (args, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/api/admin/flag-keyword/create`, args);
        return response.data;
    } catch (error: any) {
        if (error.response && error.response.data) {
            return rejectWithValue(error.response.data);
        }
        return rejectWithValue(error.message);
    }
});
// ==================AddFlagKeyword-action-end===========

// =================FlagKeyword-action-start=========
export const UpdateFlagKeyword = createAsyncThunk<
    FlagKeyword,
    { id: number; updatedData: FlagKeyword },
    { rejectValue: string }
>(
    "flagkeyworddata/UpdateFlagKeyword",
    async ({ id, updatedData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(
                `/api/admin/flag-keyword/update/${id}`,
                updatedData
            );
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue(error.message);
        }
    }
);
// =================FlagKeyword-action-end=========

// ================Delete-Flagkeyword-start=========
export const DeleteFlagKeyword = createAsyncThunk<
    { id: number },
    { id: number },
    { rejectValue: string }
>(
    "flagkeyworddata/DeleteFlagKeyword",
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.delete(`/api/admin/flag-keyword/${id}`);
            return data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return error.response.data;
            }
            return error.message;
        }
    }
);
// ================Delete-Flagkeyword-end=========

const initialState: FlagKeywordState = {
    flagkeyworddata: [],
    info: null,
    error: null,
    loading: false,
};

const flagkeywordSlice = createSlice({
    name: "Flagkeyword",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ===============get-all-Flagketword-slice-start============
            .addCase(GetallFlagKeyword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                GetallFlagKeyword.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        data: FlagKeyword[];
                        info: {
                            count: number;
                            current_page: number;
                            limit: number;
                            total_page: number;
                        };
                    }>
                ) => {
                    state.loading = false;
                    state.flagkeyworddata = action.payload.data;
                    state.info = action.payload.info;
                }
            )
            .addCase(
                GetallFlagKeyword.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || "An error occurred";
                }
            );
        // ===============get-all-Flagketword-slice-end============


        // =================add-flagkeyword-slice-start============
        builder
            .addCase(AddFlagKeyword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                AddFlagKeyword.fulfilled,
                (state, action: PayloadAction<FlagKeyword>) => {
                    state.loading = false;
                    state.flagkeyworddata.push(action.payload);
                }
            )
            .addCase(
                AddFlagKeyword.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || "Failed to add FlagKeyword";
                }
            );
        // =================add-flagkeyword-slice-end============

        // =================Update flagkeyword-slice-start============
        builder
            .addCase(UpdateFlagKeyword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                UpdateFlagKeyword.fulfilled,
                (state, action: PayloadAction<FlagKeyword>) => {
                    state.loading = false;
                    const index = state.flagkeyworddata.findIndex(
                        (item) => item.id === action.payload.id
                    );
                    if (index !== -1) {
                        state.flagkeyworddata[index] = action.payload;
                    }
                }
            )
            .addCase(
                UpdateFlagKeyword.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || "Failed to update flagkeyword";
                }
            );
        // =================Update flagkeyword-slice-start============

        // =================Delete flagkeyword-slice-start============
        builder
            .addCase(DeleteFlagKeyword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                DeleteFlagKeyword.fulfilled,
                (state, action: PayloadAction<{ id: number }>) => {
                    state.loading = false;
                }
            )
            .addCase(
                DeleteFlagKeyword.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || "Failed to delete FlagKeyword";
                }
            );
        // =================Delete flagkeyword-slice-end============
    },
});

export default flagkeywordSlice.reducer;
